import React, { useEffect, useState } from "react";
import { api, ROLE_LABELS } from "@/lib/api";
import { PageHeader, PrimaryButton, SecondaryButton, TextField, SelectField, EmptyState } from "@/components/ui-kit";
import { UserPlus, Trash2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const { user: current } = useAuth();
  const canManage = ["admin", "dirigeant"].includes(current?.role);
  const canDelete = current?.role === "admin";

  const load = () => {
    setLoading(true);
    api.get("/users").then((r) => setUsers(r.data)).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    await api.delete(`/users/${id}`);
    load();
    toast.success("Utilisateur supprimé");
  };

  return (
    <div className="p-8" data-testid="users-page">
      <Toaster position="top-right" richColors />
      <PageHeader title="Utilisateurs" subtitle="Gestion des accès et des rôles"
        actions={canManage && (
          <PrimaryButton onClick={() => setShowForm(true)} data-testid="new-user-button">
            <span className="flex items-center gap-2"><UserPlus className="w-4 h-4" /> Nouvel utilisateur</span>
          </PrimaryButton>
        )} />

      <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
        {loading ? <div className="p-10 text-center text-slate-500">Chargement…</div> :
          users.length === 0 ? <EmptyState icon={UserPlus} title="Aucun utilisateur" /> :
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-slate-50"><tr>
              <th className="text-left px-4 py-3 font-semibold uppercase text-xs">Nom</th>
              <th className="text-left px-4 py-3 font-semibold uppercase text-xs">Email</th>
              <th className="text-left px-4 py-3 font-semibold uppercase text-xs">Rôle</th>
              <th className="text-left px-4 py-3 font-semibold uppercase text-xs">Créé le</th>
              <th className="text-right px-4 py-3 font-semibold uppercase text-xs">Actions</th>
            </tr></thead>
            <tbody>{users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-[#111111]">
                  {u.name}
                  {u.role === "vendeur" && u.accepte_prospects === false && (
                    <span className="ml-2 badge-status text-amber-700 bg-amber-50 border-amber-300" data-testid={`badge-refuse-${u.id}`}>Ne reçoit plus</span>
                  )}
                </td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3">
                  <span className="badge-status text-slate-700 bg-slate-100 border-slate-300">{ROLE_LABELS[u.role] || u.role}</span>
                </td>
                <td className="px-4 py-3 text-slate-500">{u.created_at?.slice(0,10)}</td>
                <td className="px-4 py-3 text-right">
                  {canDelete && u.id !== current?.id && (
                    <button onClick={() => remove(u.id)} className="text-red-600 hover:text-red-800" data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>))}
            </tbody>
          </table>}
      </div>

      {showForm && <UserForm onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); toast.success("Utilisateur créé"); }} />}
    </div>
  );
}

function UserForm({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "vendeur" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      onCreated();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm border border-slate-200 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="user-form-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-[#111111]" style={{fontFamily:"Cabinet Grotesk"}}>Nouvel utilisateur</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <TextField label="Nom complet *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required testid="user-name" />
          <TextField label="Email *" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required testid="user-email" />
          <TextField label="Mot de passe *" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required testid="user-password" />
          <SelectField label="Rôle *" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} testid="user-role"
            options={Object.entries(ROLE_LABELS).map(([v, l]) => ({ value: v, label: l }))} />
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={onClose}>Annuler</SecondaryButton>
            <PrimaryButton type="submit" disabled={saving} data-testid="submit-user-button">{saving ? "…" : "Créer"}</PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
}
